<script setup>
import { ref, reactive, computed, onMounted, onUnmounted, watch } from 'vue'
import { useFormulario } from '@/composables/useFormulario'
import { reservarZIndex, liberarZIndex, BASE_ZINDEX } from '@/composables/useZIndex'
import api from '@/services/api'
import { marked } from 'marked'
import FormularioCadastro from '@/components/base/formulario/FormularioCadastro.vue'
import CampoInput from '@/components/base/campos/CampoInput.vue'
import CampoBuscaLista from '@/components/base/campos/CampoBuscaLista.vue'
import CampoBusca from '@/components/base/campos/CampoBusca.vue'
import ModalFormularioV2 from '@/components/base/modal/ModalFormularioV2.vue'
import SeletorComponenteVue from '@/components/base/seletor/SeletorComponenteVue.vue'
import { criarConfigBuscaDocumentacaoTela } from '@/search-definitions/documentacaoTelaSearch'

const props = defineProps({
  itemParaEditar: {
    type: Object,
    default: null
  },
  modeloVazio: {
    type: Object,
    default: () => ({
      idDocumentacaoTela: null,
      idDocumentacao: '',
      modulo: '',
      tela: '',
      titulo: '',
      conteudoMarkdown: '',
      contextoNegocio: null,
      palavrasChave: null,
      lstTipoTela: null,
      versao: '1.0',
      ativo: true,
      permissoes: [],
      relacionamentos: []
    })
  }
})

// --- Seletor de Componentes Vue ---
const showSeletorComponente = ref(false)

const abrirSeletor = () => {
  showSeletorComponente.value = true
}

const onComponenteSelecionado = (comp) => {
  if (!comp) return
  item.value.idDocumentacao = comp.valorRetorno
  item.value.tela = comp.nomeArquivo
  item.value.modulo = comp.modulo

  if (comp.jaDocumentado) {
    emit('show-notification', {
      message: `"${comp.valorRetorno}" já possui documentação. Deseja atualizá-la?`,
      type: 'warning'
    })
  }
}

const emit = defineEmits(['salvo', 'cancelado', 'show-notification'])

const accordionState = reactive({
  identificacao: true,
  conteudo: true,
  permissoes: false,
  relacionamentos: false
})

const {
  item,
  isLoading,
  isEditing,
  submitForm,
  handleCancel,
  initializeForm
} = useFormulario(props, emit, { idKey: 'idDocumentacaoTela' })

const tituloModulo = computed(() => {
  return 'Documentação de Tela'
})

const editorRef = ref(null)

const caracteres = computed(() => {
  if (!item.value || !item.value.conteudoMarkdown) {
    console.warn('[DocumentacaoTelaCadastro] item.value.conteudoMarkdown está undefined. item.value:', JSON.stringify(item.value))
    return 0
  }
  return item.value.conteudoMarkdown.length
})

const previewHtml = computed(() => {
  if (!item.value.conteudoMarkdown) return '<p class="text-muted">Pré-visualização vazia...</p>'
  try {
    return marked.parse(item.value.conteudoMarkdown)
  } catch {
    return '<p class="text-danger">Erro ao renderizar Markdown</p>'
  }
})

const configBuscaDocTela = computed(() => criarConfigBuscaDocumentacaoTela())
const modalRelacionamento = ref(false)
const relacionamentoEmEdicao = ref(null)
const isSavingRelacionamento = ref(false)

const colunasPermissoes = ref([
  {
    field: 'idRegraAcessoPermissao',
    label: 'Permissão',
    width: 300,
    required: true,
    type: 'select',
    options: [], // preenchido via API
    placeholder: 'Buscar permissão...'
  },
  { field: 'chave', label: 'Chave', width: 200, readonly: true },
  { field: 'modulo', label: 'Módulo', width: 150, readonly: true }
])

// Carregar dados completos quando for edição (GetById traz relacionamentos e permissões)
const carregarDadosEdicao = async () => {
  if (!isEditing.value || !item.value?.idDocumentacaoTela) return
  try {
    const response = await api.get(`/DocumentacaoTela/${item.value.idDocumentacaoTela}`, { _suppressLoading: true })
    if (response.data) {
      // Preserva campos de controle que não vêm do backend
      const tempId = item.value.idDocumentacaoTela
      Object.assign(item.value, response.data)
      item.value.idDocumentacaoTela = tempId
    }
  } catch (error) {
    console.warn('[DocumentacaoTelaCadastro] Erro ao carregar dados completos:', error)
  }
}

// Carregar permissões disponíveis via API
onMounted(async () => {
  await carregarDadosEdicao()
  try {
    const response = await api.get('/RegrasAcesso?pageSize=100')
    const regras = response.data || []
    // Extrair todas as permissões de todas as regras
    const todasPermissoes = []
    for (const regra of regras) {
      if (regra.permissoes) {
        for (const perm of regra.permissoes) {
          const descricao = `${regra.modulo} • ${perm.chave}`
          todasPermissoes.push({
            value: perm.idRegraAcessoPermissao,
            label: descricao,
            textoLista: descricao,
            idRegraAcessoPermissao: perm.idRegraAcessoPermissao,
            chave: perm.chave,
            modulo: regra.modulo
          })
        }
      }
    }
    colunasPermissoes.value[0].options = todasPermissoes
  } catch (error) {
    console.warn('[DocumentacaoTelaCadastro] Erro ao carregar permissões:', error)
  }
})

// === Handlers do Modal de Relacionamentos ===
const iniciarNovoRelacionamento = () => {
  if (!item.value?.idDocumentacaoTela) {
    emit('show-notification', {
      message: 'Salve a documentação antes de adicionar relacionamentos.',
      type: 'warning'
    })
    return
  }
  relacionamentoEmEdicao.value = {
    _isNew: true,
    idDocumentacaoDestino: '',
    tituloDestino: '',
    tipoRelacionamento: 'FK',
    nivelRelacao: 2,
    descricao: ''
  }
  modalRelacionamento.value = true
}

const editarRelacionamento = (rel, idx) => {
  relacionamentoEmEdicao.value = {
    ...rel,
    _index: idx,
    _isNew: false
  }
  modalRelacionamento.value = true
}

const excluirRelacionamento = async (rel, idx) => {
  if (!rel.idDocumentacaoTelaRelacionada) {
    item.value.relacionamentos.splice(idx, 1)
    return
  }
  try {
    await api.delete(`/DocumentacaoTelaRelacionada/${rel.idDocumentacaoTelaRelacionada}`)
    item.value.relacionamentos.splice(idx, 1)
    emit('show-notification', { message: 'Relacionamento removido com sucesso.', type: 'success' })
  } catch (error) {
    const data = error.response?.data
    let msg = 'Erro ao remover relacionamento.'
    if (typeof data === 'string') msg = data
    else if (data?.message) msg = data.message
    else if (data?.title) msg = data.title
    emit('show-notification', { message: msg, type: 'error' })
  }
}

const salvarRelacionamento = async () => {
  const dados = relacionamentoEmEdicao.value
  if (!dados.idDocumentacaoDestino || !dados.tipoRelacionamento) {
    emit('show-notification', { message: 'Preencha a Documentação de destino e o Tipo.', type: 'warning' })
    return
  }

  isSavingRelacionamento.value = true
  try {
    const payload = {
      idDocumentacaoDestino: dados.idDocumentacaoDestino,
      nivelRelacao: Number(dados.nivelRelacao) || 2,
      tipoRelacionamento: dados.tipoRelacionamento,
      descricao: dados.descricao || null
    }

    if (dados._isNew) {
      const res = await api.post('/DocumentacaoTelaRelacionada', payload, {
        headers: { IdDocumentacaoTelaOrigem: String(item.value.idDocumentacaoTela) }
      })
      item.value.relacionamentos.push(res.data)
      emit('show-notification', { message: 'Relacionamento adicionado com sucesso.', type: 'success' })
    } else {
      const res = await api.put(`/DocumentacaoTelaRelacionada/${dados.idDocumentacaoTelaRelacionada}`, payload)
      if (typeof dados._index === 'number') {
        item.value.relacionamentos[dados._index] = res.data
      }
      emit('show-notification', { message: 'Relacionamento atualizado com sucesso.', type: 'success' })
    }
    modalRelacionamento.value = false
  } catch (error) {
    const data = error.response?.data
    let msg = 'Erro ao salvar relacionamento.'
    if (typeof data === 'string') msg = data
    else if (data?.message) msg = data.message
    else if (data?.title) msg = data.title
    else if (data?.errors) msg = Object.values(data.errors).flat().join('; ')
    emit('show-notification', { message: msg, type: 'error' })
  } finally {
    isSavingRelacionamento.value = false
  }
}

const onDocDestinoSelecionado = (item) => {
  if (!relacionamentoEmEdicao.value) return
  relacionamentoEmEdicao.value.idDocumentacaoDestino = item.idDocumentacao
  relacionamentoEmEdicao.value.tituloDestino = item.titulo
}

const getTituloDestino = (idDocumentacaoDestino) => {
  return idDocumentacaoDestino
}

const getTipoLabel = (tipo) => {
  const labels = { FK: 'FK (Chave Estrangeira)', COMPOSICAO: 'Composição', HERANCA: 'Herança', ESTRUTURAL: 'Estrutural (Snapshot)' }
  return labels[tipo] || tipo
}

const getNivelLabel = (nivel) => {
  return nivel === 2 ? '2 - Secundária' : '3 - Terciária'
}

// === Handlers do ExcelGrid de permissões ===
const adicionarPermissao = () => {
  if (!Array.isArray(item.value.permissoes)) item.value.permissoes = []
  item.value.permissoes.push({
    _tempId: Date.now() + Math.random(),
    idRegraAcessoPermissao: '',
    chave: '',
    modulo: ''
  })
}

const removerPermissao = ({ row }) => {
  const lista = item.value.permissoes
  if (!Array.isArray(lista)) return
  const idx = lista.findIndex(p =>
    (row._tempId && p._tempId === row._tempId) ||
    (row.idRegraAcessoPermissao && String(p.idRegraAcessoPermissao) === String(row.idRegraAcessoPermissao))
  )
  if (idx >= 0) lista.splice(idx, 1)
}

const onPermissaoCellEdit = ({ row, field, value }) => {
  if (field !== 'idRegraAcessoPermissao') return
  const opt = colunasPermissoes.value[0].options.find(o => String(o.value) === String(value))
  if (opt) {
    row.chave = opt.chave
    row.modulo = opt.modulo
  } else {
    row.chave = ''
    row.modulo = ''
  }
}

const cancelarPermissoes = () => {
  if (!Array.isArray(item.value.permissoes)) return
  item.value.permissoes = item.value.permissoes.filter(p => !p._tempId)
}

const montarPayload = () => {
  return {
    idDocumentacao: item.value.idDocumentacao,
    modulo: item.value.modulo,
    tela: item.value.tela,
    titulo: item.value.titulo,
    conteudoMarkdown: item.value.conteudoMarkdown,
    contextoNegocio: item.value.contextoNegocio || null,
    palavrasChave: item.value.palavrasChave || null,
    lstTipoTela: item.value.lstTipoTela || null,
    versao: item.value.versao,
    ativo: item.value.ativo !== false,
    idsRegraAcessoPermissao: (Array.isArray(item.value.permissoes) ? item.value.permissoes : [])
      .map(p => Number(p.idRegraAcessoPermissao))
      .filter(id => !Number.isNaN(id) && id > 0)
  }
}

const validarFormulario = () => {
  if (!item.value.idDocumentacao.trim()) {
    emit('show-notification', { message: 'O ID da Documentação é obrigatório.', type: 'warning' })
    return false
  }
  if (!item.value.modulo.trim()) {
    emit('show-notification', { message: 'O Módulo é obrigatório.', type: 'warning' })
    return false
  }
  if (!item.value.tela.trim()) {
    emit('show-notification', { message: 'A Tela é obrigatória.', type: 'warning' })
    return false
  }
  if (!item.value.titulo.trim()) {
    emit('show-notification', { message: 'O Título é obrigatório.', type: 'warning' })
    return false
  }
  if (!item.value.conteudoMarkdown.trim()) {
    emit('show-notification', { message: 'O Conteúdo Markdown é obrigatório.', type: 'warning' })
    return false
  }
  return true
}

const submitFormCustom = async () => {
  if (!validarFormulario()) return false
  const payload = montarPayload()
  await submitForm('DocumentacaoTela', 'idDocumentacaoTela', payload)
}

const submitFormContinuar = async () => {
  if (!validarFormulario()) return false
  const payload = montarPayload()
  isLoading.value = true
  try {
    const response = isEditing.value
      ? await api.put(`/DocumentacaoTela/${item.value.idDocumentacaoTela}`, payload)
      : await api.post('/DocumentacaoTela', payload)

    if (response?.data) {
      initializeForm(response.data)
    }
    emit('show-notification', { message: 'Documentação salva com sucesso.', type: 'success' })
  } catch (error) {
    const data = error.response?.data
    let msg = 'Erro ao salvar documentação.'
    if (typeof data === 'string') msg = data
    else if (data?.message) msg = data.message
    else if (data?.title) msg = data.title
    else if (data?.errors) msg = Object.values(data.errors).flat().join('; ')
    emit('show-notification', { message: msg, type: 'error' })
  } finally {
    isLoading.value = false
  }
}

const handleCancelCustom = () => {
  handleCancel()
}

const inserirMarkdown = (prefixo, sufixo) => {
  const textarea = editorRef.value
  if (!textarea) return

  const start = textarea.selectionStart
  const end = textarea.selectionEnd
  const textoSelecionado = item.value.conteudoMarkdown.substring(start, end)

  item.value.conteudoMarkdown = item.value.conteudoMarkdown.substring(0, start) +
    prefixo +
    textoSelecionado +
    sufixo +
    item.value.conteudoMarkdown.substring(end)

  const novoCursor = start + prefixo.length + textoSelecionado.length + sufixo.length
  textarea.focus()
  textarea.setSelectionRange(novoCursor, novoCursor)
}

const inserirTab = () => {
  const textarea = editorRef.value
  if (!textarea) return
  const start = textarea.selectionStart
  item.value.conteudoMarkdown = item.value.conteudoMarkdown.substring(0, start) + '  ' + item.value.conteudoMarkdown.substring(textarea.selectionEnd)
  textarea.focus()
  textarea.setSelectionRange(start + 2, start + 2)
}

// === Preview do Markdown com Telas Relacionadas ===
const previewComRelacionamentos = ref(false)
const previewMarkdownCarregado = ref('')
const isLoadingPreview = ref(false)

const carregarPreviewComRelacionamentos = async () => {
  if (!item.value?.idDocumentacao) {
    emit('show-notification', { message: 'Salve a documentação antes de visualizar o preview com relacionamentos.', type: 'warning' })
    return
  }
  isLoadingPreview.value = true
  try {
    const response = await api.get(`/DocumentacaoTela/${item.value.idDocumentacao}/preview-markdown`, { _suppressLoading: true })
    previewMarkdownCarregado.value = response.data?.conteudoMarkdown || ''
    previewComRelacionamentos.value = true
  } catch (error) {
    const data = error.response?.data
    let msg = 'Erro ao carregar preview com relacionamentos.'
    if (typeof data === 'string') msg = data
    else if (data?.message) msg = data.message
    emit('show-notification', { message: msg, type: 'error' })
  } finally {
    isLoadingPreview.value = false
  }
}

// === Bloco de Documentações (como o Helper recebe) ===
const blocoDocumentacoes = ref(false)
const blocoJsonCarregado = ref('')

// --- Empilhamento (z-index) via registry central ---
const zIndexOverlay = ref(BASE_ZINDEX)
const tokenOverlay = ref(null)
const overlayAberto = computed(() => previewComRelacionamentos.value || blocoDocumentacoes.value)

watch(overlayAberto, (aberto) => {
  if (aberto) {
    const { token, zIndex: z } = reservarZIndex()
    tokenOverlay.value = token
    zIndexOverlay.value = z
  } else {
    liberarZIndex(tokenOverlay.value)
    tokenOverlay.value = null
  }
})

onUnmounted(() => {
  liberarZIndex(tokenOverlay.value)
  tokenOverlay.value = null
})
const isLoadingBloco = ref(false)

const carregarBlocoDocumentacoes = async () => {
  if (!item.value?.idDocumentacao) {
    emit('show-notification', { message: 'Salve a documentação antes de visualizar o bloco.', type: 'warning' })
    return
  }
  isLoadingBloco.value = true
  try {
    const response = await api.get(`/DocumentacaoTela/${item.value.idDocumentacao}/bloco`, { _suppressLoading: true })
    blocoJsonCarregado.value = JSON.stringify(response.data, null, 2)
    blocoDocumentacoes.value = true
  } catch (error) {
    const data = error.response?.data
    let msg = 'Erro ao carregar bloco de documentações.'
    if (typeof data === 'string') msg = data
    else if (data?.message) msg = data.message
    emit('show-notification', { message: msg, type: 'error' })
  } finally {
    isLoadingBloco.value = false
  }
}

const copiarBlocoJson = async () => {
  try {
    await navigator.clipboard.writeText(blocoJsonCarregado.value)
    emit('show-notification', { message: 'JSON do bloco copiado para a área de transferência.', type: 'success' })
  } catch {
    emit('show-notification', { message: 'Não foi possível copiar o JSON.', type: 'error' })
  }
}
</script>

<template>
  <div class="documentacao-tela-form-wrapper">
    <FormularioCadastro
      :module-name="tituloModulo"
      :is-editing="isEditing"
      :is-loading="isLoading"
      :entity-info="isEditing ? { id: item.idDocumentacaoTela, name: item.idDocumentacao, nameLabel: 'ID' } : null"
      :accordion-state="accordionState"
      @submit="submitFormCustom"
      @submit-continuar="submitFormContinuar"
      @cancelar="handleCancelCustom"
    >
      <!-- FRAME 1: IDENTIFICAÇÃO -->
      <div class="card mb-4">
        <div class="card-header bg-body-tertiary cursor-pointer d-flex justify-content-between align-items-center" @click="accordionState.identificacao = !accordionState.identificacao">
          <h5 class="mb-0"><i class="bi bi-tags me-2 text-primary"></i> Identificação</h5>
          <i :class="['bi', accordionState.identificacao ? 'bi-chevron-up' : 'bi-chevron-down', 'text-secondary']"></i>
        </div>
        <div class="card-body" v-show="accordionState.identificacao">
          <div class="row g-3">
            <div class="col-md-6">
              <label class="form-label">Componente Vue <span class="text-danger">*</span></label>
              <div class="input-group">
                <input type="text" class="form-control" v-model="item.idDocumentacao"
                       readonly placeholder="Selecione um componente...">
                <button type="button" class="btn btn-outline-primary" @click="abrirSeletor">
                  <i class="bi bi-folder2-open"></i> Selecionar
                </button>
              </div>
              <small class="text-muted">Caminho relativo do arquivo .vue (ex: logistica/embarques/EmbarquesAdministrar)</small>
            </div>
            <div class="col-md-3">
              <CampoInput
                id="modulo"
                label="Módulo"
                type="text"
                v-model="item.modulo"
                required
                placeholder="Ex: Fiscal"
              />
            </div>
            <div class="col-md-3">
              <CampoInput
                id="tela"
                label="Tela"
                type="text"
                v-model="item.tela"
                required
                placeholder="Ex: NcmAdministrar"
              />
            </div>
            <div class="col-md-3">
              <CampoBuscaLista
                id="lstTipoTela"
                label="Tipo de Tela"
                v-model="item.lstTipoTela"
                tabela="DocumentacaoTela"
              />
            </div>
            <div class="col-md-3">
              <CampoInput
                id="versao"
                label="Versão"
                type="text"
                v-model="item.versao"
                placeholder="1.0"
              />
            </div>
            <div class="col-12">
              <CampoInput
                id="titulo"
                label="Título"
                type="text"
                v-model="item.titulo"
                required
                placeholder="Ex: Administração de NCMs"
              />
            </div>
            <div class="col-md-3">
              <CampoSelect
                id="ativo"
                label="Ativo"
                v-model="item.ativo"
                :options="[
                  { value: true, label: 'Sim' },
                  { value: false, label: 'Não' }
                ]"
                option-value="value"
                option-label="label"
                required
              />
            </div>
            <div class="col-12">
              <CampoInput
                id="contextoNegocio"
                label="Contexto de Negócio"
                type="textarea"
                v-model="item.contextoNegocio"
                placeholder="Descreva o objetivo, regras de negócio e finalidade da tela..."
              />
              <small class="text-muted">Usado para busca semântica e filtro de menu por contexto.</small>
            </div>
            <div class="col-12">
              <CampoInput
                id="palavrasChave"
                label="Palavras-Chave"
                type="text"
                v-model="item.palavrasChave"
                placeholder="Ex: NCM, classificação fiscal, tributos, importação"
              />
              <small class="text-muted">Palavras-chave separadas por vírgulas para busca e indexação.</small>
            </div>
          </div>
        </div>
      </div>

      <!-- FRAME 2: CONTEÚDO MARKDOWN -->
      <div class="card mb-4">
        <div class="card-header bg-body-tertiary cursor-pointer d-flex justify-content-between align-items-center" @click="accordionState.conteudo = !accordionState.conteudo">
          <h5 class="mb-0"><i class="bi bi-markdown me-2 text-primary"></i> Conteúdo Markdown
            <span class="text-muted small ms-2">({{ caracteres }} caracteres)</span>
          </h5>
          <div class="d-flex align-items-center gap-2">
            <button
              type="button"
              class="btn btn-sm btn-outline-info py-0"
              @click.stop="carregarPreviewComRelacionamentos"
              :disabled="isLoadingPreview || !item?.idDocumentacao"
              title="Visualizar Markdown com Telas Relacionadas injetadas"
            >
              <i :class="['bi', isLoadingPreview ? 'bi-arrow-repeat spin' : 'bi-diagram-3']"></i>
              Preview c/ Relac.
            </button>
            <button
              type="button"
              class="btn btn-sm btn-outline-secondary py-0"
              @click.stop="carregarBlocoDocumentacoes"
              :disabled="isLoadingBloco || !item?.idDocumentacao"
              title="Visualizar o bloco completo de documentações (primária + relacionadas) como o Helper recebe"
            >
              <i :class="['bi', isLoadingBloco ? 'bi-arrow-repeat spin' : 'bi-collection']"></i>
              Bloco
            </button>
            <i :class="['bi', accordionState.conteudo ? 'bi-chevron-up' : 'bi-chevron-down', 'text-secondary']"></i>
          </div>
        </div>
        <div class="card-body" v-show="accordionState.conteudo">
          <!-- Barra de ferramentas -->
          <div class="markdown-toolbar mb-1">
            <button type="button" class="btn-toolbar" @click="inserirMarkdown('**', '**')" title="Negrito"><strong>B</strong></button>
            <button type="button" class="btn-toolbar" @click="inserirMarkdown('*', '*')" title="Itálico"><em>I</em></button>
            <button type="button" class="btn-toolbar" @click="inserirMarkdown('# ', '')" title="Título H1">H1</button>
            <button type="button" class="btn-toolbar" @click="inserirMarkdown('## ', '')" title="Título H2">H2</button>
            <button type="button" class="btn-toolbar" @click="inserirMarkdown('### ', '')" title="Título H3">H3</button>
            <span class="toolbar-divider"></span>
            <button type="button" class="btn-toolbar" @click="inserirMarkdown('- ', '')" title="Lista não ordenada"><i class="bi bi-list-ul"></i></button>
            <button type="button" class="btn-toolbar" @click="inserirMarkdown('1. ', '')" title="Lista ordenada"><i class="bi bi-list-ol"></i></button>
            <button type="button" class="btn-toolbar" @click="inserirMarkdown('> ', '')" title="Citação"><i class="bi bi-quote"></i></button>
            <span class="toolbar-divider"></span>
            <button type="button" class="btn-toolbar" @click="inserirMarkdown('[', '](url)')" title="Link"><i class="bi bi-link-45deg"></i></button>
            <button type="button" class="btn-toolbar" @click="inserirMarkdown('![', '](url)')" title="Imagem"><i class="bi bi-image"></i></button>
            <button type="button" class="btn-toolbar" @click="inserirMarkdown('```\n', '\n```')" title="Bloco de código"><i class="bi bi-code-slash"></i></button>
            <button type="button" class="btn-toolbar" @click="inserirMarkdown('---\n', '')" title="Linha horizontal"><i class="bi bi-dash-lg"></i></button>
            <span class="toolbar-divider"></span>
            <button type="button" class="btn-toolbar" @click="inserirMarkdown('| ', ' |')" title="Tabela"><i class="bi bi-table"></i></button>
          </div>

          <!-- Editor + Preview lado a lado -->
          <div class="markdown-editor-wrapper">
            <div class="markdown-editor-pane">
              <textarea ref="editorRef" class="form-control markdown-textarea"
                        v-model="item.conteudoMarkdown"
                        placeholder="Digite o conteúdo em Markdown..."
                        @keydown.tab.prevent="inserirTab"></textarea>
            </div>
            <div class="markdown-preview-pane">
              <div class="markdown-preview-header">Pré-visualização</div>
              <div class="markdown-preview-content" v-html="previewHtml"></div>
            </div>
          </div>

          <!-- Legenda de comandos -->
          <div class="markdown-legend mt-2">
            <strong class="small">📖 Comandos:</strong>
            <span class="legend-item"><code># Título</code> Principal</span>
            <span class="legend-item"><code>## Subtítulo</code> Subtítulo</span>
            <span class="legend-item"><code>**negrito**</code> Negrito</span>
            <span class="legend-item"><code>*itálico*</code> Itálico</span>
            <span class="legend-item"><code>- item</code> Lista</span>
            <span class="legend-item"><code>1. item</code> Lista num.</span>
            <span class="legend-item"><code>[texto](url)</code> Link</span>
            <span class="legend-item"><code>```código```</code> Código</span>
            <span class="legend-item"><code>> citação</code> Citação</span>
            <span class="legend-item"><code>---</code> Linha</span>
            <span class="legend-item"><code>| Col1 | Col2 |</code> Tabela</span>
          </div>
        </div>
      </div>

      <!-- FRAME 3: DOCUMENTOS RELACIONADOS -->
      <div class="card mb-4">
        <div class="card-header bg-body-tertiary cursor-pointer d-flex justify-content-between align-items-center"
             @click="accordionState.relacionamentos = !accordionState.relacionamentos">
          <h5 class="mb-0"><i class="bi bi-diagram-3 me-2 text-info"></i> Documentos Relacionados</h5>
          <i :class="['bi', accordionState.relacionamentos ? 'bi-chevron-up' : 'bi-chevron-down', 'text-secondary']"></i>
        </div>
        <div class="card-body" v-show="accordionState.relacionamentos">
          <div class="alert alert-info border-0 shadow-sm small py-2 mb-3 d-flex align-items-center">
            <i class="bi bi-info-circle-fill me-2 fs-5"></i>
            <div>
              <strong>Tipos de Relacionamento:</strong><br>
              <strong>FK</strong> (Chave Estrangeira) — A tela atual possui uma FK lógica para a tela de destino.
              Ex: <em>TítulosFinanceirosCadastro</em> possui <code>IdParceiro</code> → <em>ParceirosAdministrar</em>.<br>
              <strong>COMPOSIÇÃO</strong> — A tela de destino é um subcomponente ou parte integrante desta tela.
              Ex: <em>ProdutosCadastro</em> contém o frame <em>Cláusulas Comerciais</em>.<br>
              <strong>HERANÇA</strong> — A tela atual é uma especialização ou extensão da tela de destino.
              Ex: <em>PurchaseOrderForm</em> estende <em>DocumentoEntradaForm</em>.<br>
              O <strong>nível</strong> indica a profundidade: <strong>2</strong> = Secundária (FK direta), <strong>3</strong> = Terciária (FK indireta).
            </div>
          </div>

          <div class="admin-table-container border-0 shadow-none mb-0">
            <table class="table admin-table m-0">
              <thead>
                <tr>
                  <th>Documentação de Destino</th>
                  <th>Título</th>
                  <th>Tipo</th>
                  <th>Nível</th>
                  <th>Descrição</th>
                  <th class="text-end" style="width: 100px;">
                    <button
                      type="button"
                      class="btn btn-sm btn-primary py-0"
                      @click="iniciarNovoRelacionamento"
                      :disabled="!item?.idDocumentacaoTela"
                      title="Novo Relacionamento"
                    >
                      <i class="bi bi-plus-lg"></i>
                    </button>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(rel, idx) in item.relacionamentos" :key="rel.idDocumentacaoTelaRelacionada || idx">
                  <td class="fw-bold">{{ rel.idDocumentacaoDestino }}</td>
                  <td>{{ rel.tituloDestino }}</td>
                  <td>{{ getTipoLabel(rel.tipoRelacionamento) }}</td>
                  <td>{{ getNivelLabel(rel.nivelRelacao) }}</td>
                  <td>{{ rel.descricao || '-' }}</td>
                  <td class="acoes-cell text-end pe-3">
                    <button type="button" class="btn-icon" @click="editarRelacionamento(rel, idx)" title="Editar">
                      <i class="bi bi-pencil-fill"></i>
                    </button>
                    <button type="button" class="btn-icon text-danger" @click="excluirRelacionamento(rel, idx)" title="Excluir">
                      <i class="bi bi-trash-fill"></i>
                    </button>
                  </td>
                </tr>
                <tr v-if="!item.relacionamentos || item.relacionamentos.length === 0">
                  <td colspan="6" class="text-center py-4 text-muted">
                    Nenhum documento relacionado cadastrado.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- MODAL DE RELACIONAMENTO -->
      <ModalFormularioV2
        v-model="modalRelacionamento"
        :titulo="relacionamentoEmEdicao?._isNew ? 'Novo Relacionamento' : 'Editar Relacionamento'"
        @confirmar="salvarRelacionamento"
        :loading="isSavingRelacionamento"
      >
        <div v-if="relacionamentoEmEdicao" class="row g-3">
          <div class="col-md-12">
            <CampoBusca
              id="idDocumentacaoDestino"
              label="Documentação de Destino"
              :configuracao-busca="configBuscaDocTela"
              @selecionado="onDocDestinoSelecionado"
              required
            />
            <small class="text-muted" v-if="relacionamentoEmEdicao.idDocumentacaoDestino">
              Título: {{ relacionamentoEmEdicao.tituloDestino || getTituloDestino(relacionamentoEmEdicao.idDocumentacaoDestino) }}
            </small>
          </div>
          <div class="col-md-6">
            <CampoSelect
              id="tipoRelacionamento"
              label="Tipo de Relacionamento"
              v-model="relacionamentoEmEdicao.tipoRelacionamento"
              :options="[
                { value: 'FK', label: 'FK (Chave Estrangeira)' },
                { value: 'COMPOSICAO', label: 'Composição' },
                { value: 'HERANCA', label: 'Herança' },
                { value: 'ESTRUTURAL', label: 'Estrutural (Snapshot)' }
              ]"
              option-value="value"
              option-label="label"
              required
            />
          </div>
          <div class="col-md-6">
            <CampoSelect
              id="nivelRelacao"
              label="Nível da Relação"
              v-model="relacionamentoEmEdicao.nivelRelacao"
              :options="[
                { value: 2, label: '2 - Secundária (FK direta)' },
                { value: 3, label: '3 - Terciária (FK indireta)' }
              ]"
              option-value="value"
              option-label="label"
              required
            />
          </div>
          <div class="col-12">
            <CampoInput
              id="descricao"
              label="Descrição (opcional)"
              v-model="relacionamentoEmEdicao.descricao"
              placeholder="Ex: Credor/Devedor do título"
              maxlength="200"
            />
          </div>
        </div>
      </ModalFormularioV2>

      <!-- FRAME 4: PERMISSÕES DE ACESSO -->
      <div class="card mb-4">
        <div class="card-header bg-body-tertiary cursor-pointer d-flex justify-content-between align-items-center"
             @click="accordionState.permissoes = !accordionState.permissoes">
          <h5 class="mb-0"><i class="bi bi-shield-lock me-2 text-warning"></i> Permissões de Acesso</h5>
          <i :class="['bi', accordionState.permissoes ? 'bi-chevron-up' : 'bi-chevron-down', 'text-secondary']"></i>
        </div>
        <div class="card-body" v-show="accordionState.permissoes">
          <p class="text-muted small mb-2">
            Selecione as permissões granulares que liberam acesso a esta tela.
          </p>
          <ExcelGrid
            :data="item.permissoes"
            :columns="colunasPermissoes"
            :allow-add-rows="true"
            :has-changes-field="'idRegraAcessoPermissao'"
            row-key="idRegraAcessoPermissao"
            empty-message="Nenhuma permissão associada."
            @add-row="adicionarPermissao"
            @delete-row="removerPermissao"
            @cell-edit="onPermissaoCellEdit"
            @cancel-all="cancelarPermissoes"
            @show-notification="(p) => emit('show-notification', p)"
          />
        </div>
      </div>
    </FormularioCadastro>
  </div>

  <!-- MODAL DE PREVIEW COM RELACIONAMENTOS -->
  <Teleport to="body">
    <div v-if="previewComRelacionamentos" class="preview-overlay" :style="{ zIndex: zIndexOverlay }" @click.self="previewComRelacionamentos = false">
      <div class="preview-modal">
        <div class="preview-modal-header">
          <h5 class="mb-0"><i class="bi bi-diagram-3 me-2 text-info"></i> Preview com Telas Relacionadas</h5>
          <button type="button" class="btn-close" @click="previewComRelacionamentos = false"></button>
        </div>
        <div class="preview-modal-body">
          <div class="preview-content" v-html="marked.parse(previewMarkdownCarregado)"></div>
        </div>
        <div class="preview-modal-footer">
          <button type="button" class="btn btn-secondary" @click="previewComRelacionamentos = false">Fechar</button>
        </div>
      </div>
    </div>
  </Teleport>

  <!-- MODAL DE BLOCO DE DOCUMENTAÇÕES (como o Helper recebe) -->
  <Teleport to="body">
    <div v-if="blocoDocumentacoes" class="preview-overlay" :style="{ zIndex: zIndexOverlay }" @click.self="blocoDocumentacoes = false">
      <div class="preview-modal preview-modal-lg">
        <div class="preview-modal-header">
          <h5 class="mb-0"><i class="bi bi-collection me-2 text-primary"></i> Bloco de Documentações (primária + relacionadas)</h5>
          <button type="button" class="btn-close" @click="blocoDocumentacoes = false"></button>
        </div>
        <div class="preview-modal-body">
          <pre class="bloco-json mb-0"><code>{{ blocoJsonCarregado }}</code></pre>
        </div>
        <div class="preview-modal-footer">
          <button type="button" class="btn btn-outline-secondary btn-sm me-1"
                  @click="copiarBlocoJson"
                  title="Copiar JSON do bloco para a área de transferência">
            <i class="bi bi-clipboard me-1"></i>Copiar JSON
          </button>
          <button type="button" class="btn btn-secondary" @click="blocoDocumentacoes = false">Fechar</button>
        </div>
      </div>
    </div>
  </Teleport>

  <!-- MODAL DE SELEÇÃO DE COMPONENTE VUE -->
  <SeletorComponenteVue
    v-model="showSeletorComponente"
    return-type="fileName"
    :include-extension="false"
    @selecionado="onComponenteSelecionado"
  />
</template>

<style scoped>
.documentacao-tela-form-wrapper {
  --ct-34495e: #34495E;
  --ct-34495e-border: rgba(52, 73, 94, 0.25);
  --ct-34495e-bg-subtle: rgba(52, 73, 94, 0.06);
  --ct-34495e-bg-subtle-dark: rgba(52, 73, 94, 0.2);
}

/* Toolbar */
.markdown-toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 2px;
  padding: 6px 8px;
  background: var(--bs-tertiary-bg);
  border: 1px solid var(--bs-border-color);
  border-bottom: none;
  border-radius: 4px 4px 0 0;
  align-items: center;
}

.btn-toolbar {
  background: none;
  border: 1px solid transparent;
  padding: 4px 8px;
  cursor: pointer;
  border-radius: 3px;
  font-size: 0.85rem;
  color: var(--bs-body-color);
  transition: all 0.15s ease;
  min-width: 32px;
  text-align: center;
}

.btn-toolbar:hover {
  background: var(--bs-secondary-bg);
  border-color: var(--bs-border-color);
}

.toolbar-divider {
  width: 1px;
  height: 20px;
  background: var(--bs-border-color);
  margin: 0 4px;
}

/* Editor + Preview */
.markdown-editor-wrapper {
  display: flex;
  border: 1px solid var(--bs-border-color);
  border-radius: 0 0 4px 4px;
  min-height: 400px;
}

.markdown-editor-pane {
  flex: 1;
  display: flex;
  border-right: 1px solid var(--bs-border-color);
}

.markdown-textarea {
  flex: 1;
  border: none !important;
  border-radius: 0 !important;
  resize: none;
  font-family: 'Consolas', 'Courier New', monospace;
  font-size: 0.875rem;
  line-height: 1.6;
  padding: 12px;
  min-height: 400px;
}

.markdown-textarea:focus {
  box-shadow: none !important;
}

.markdown-preview-pane {
  flex: 1;
  display: flex;
  flex-direction: column;
  background: var(--bs-body-bg);
}

.markdown-preview-header {
  padding: 6px 12px;
  font-size: 0.75rem;
  color: var(--bs-secondary-color);
  background: var(--bs-tertiary-bg);
  border-bottom: 1px solid var(--bs-border-color);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 600;
}

.markdown-preview-content {
  flex: 1;
  padding: 12px;
  overflow-y: auto;
  font-size: 0.9rem;
  line-height: 1.6;
}

.markdown-preview-content :deep(h1),
.markdown-preview-content :deep(h2),
.markdown-preview-content :deep(h3) {
  margin-top: 1rem;
  margin-bottom: 0.5rem;
}

.markdown-preview-content :deep(p) {
  margin-bottom: 0.5rem;
}

.markdown-preview-content :deep(code) {
  background: var(--bs-tertiary-bg);
  padding: 2px 6px;
  border-radius: 3px;
  font-size: 0.85em;
}

.markdown-preview-content :deep(pre) {
  background: var(--bs-tertiary-bg);
  padding: 12px;
  border-radius: 4px;
  overflow-x: auto;
}

.markdown-preview-content :deep(pre code) {
  background: none;
  padding: 0;
}

.markdown-preview-content :deep(blockquote) {
  border-left: 4px solid var(--bs-primary);
  padding-left: 12px;
  color: var(--bs-secondary-color);
  margin: 0.5rem 0;
}

.markdown-preview-content :deep(table) {
  width: 100%;
  border-collapse: collapse;
  margin: 0.5rem 0;
}

.markdown-preview-content :deep(th),
.markdown-preview-content :deep(td) {
  border: 1px solid var(--bs-border-color);
  padding: 6px 10px;
  text-align: left;
}

.markdown-preview-content :deep(th) {
  background: var(--bs-tertiary-bg);
  font-weight: 600;
}

.markdown-preview-content :deep(img) {
  max-width: 100%;
  border-radius: 4px;
}

.markdown-preview-content :deep(ul),
.markdown-preview-content :deep(ol) {
  padding-left: 1.5rem;
  margin-bottom: 0.5rem;
}

.markdown-preview-content :deep(hr) {
  margin: 1rem 0;
  border-color: var(--bs-border-color);
}

/* Legenda */
.markdown-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 16px;
  padding: 10px 12px;
  background: var(--bs-tertiary-bg);
  border: 1px solid var(--bs-border-color);
  border-radius: 4px;
  font-size: 0.8rem;
  align-items: center;
}

.legend-item {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: var(--bs-secondary-color);
}

.legend-item code {
  background: var(--bs-secondary-bg);
  padding: 1px 5px;
  border-radius: 3px;
  font-size: 0.85em;
  color: var(--bs-body-color);
}

@media (max-width: 991.98px) {
  .markdown-editor-wrapper {
    flex-direction: column;
    min-height: 300px;
  }

  .markdown-editor-pane {
    border-right: none;
    border-bottom: 1px solid var(--bs-border-color);
    min-height: 250px;
  }

  .markdown-preview-pane {
    min-height: 250px;
  }

  .markdown-legend {
    gap: 4px 8px;
  }
}

/* Admin table styles (for relacionamentos grid) */
:deep(.admin-table-container) {
  background-color: transparent;
  border: 1px solid var(--bs-border-color-translucent);
  border-radius: 0.375rem;
  overflow-x: auto;
}
:deep(.admin-table thead th) {
  background-color: var(--bs-tertiary-bg);
  color: var(--bs-heading-color);
  font-weight: 600;
  padding: 0.5rem 0.75rem;
  border-bottom: 2px solid var(--bs-border-color-translucent);
}
:deep(.admin-table td) {
  border-bottom: 1px solid var(--bs-border-color-translucent);
  color: var(--bs-body-color);
  vertical-align: middle;
}
:deep(.acoes-cell) {
  text-align: right;
  vertical-align: middle;
  padding-right: 0.5rem;
}
:deep(.btn-icon) {
  background: none;
  border: none;
  color: #6c7d8e;
  padding: 0.4rem;
  transition: 0.2s;
}
:deep(.btn-icon:hover) {
  color: #212934;
}
[data-bs-theme="dark"] :deep(.btn-icon:hover) {
  color: #fff;
}

/* Preview Modal com Telas Relacionadas */
.preview-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
}

.preview-modal {
  background: var(--bs-body-bg);
  border-radius: 8px;
  width: 90%;
  max-width: 900px;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
}

.preview-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid var(--bs-border-color);
  flex-shrink: 0;
}

.preview-modal-body {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
}

.preview-content {
  font-size: 0.9rem;
  line-height: 1.6;
}

.preview-content :deep(h1),
.preview-content :deep(h2),
.preview-content :deep(h3) {
  margin-top: 1rem;
  margin-bottom: 0.5rem;
}

.preview-content :deep(p) {
  margin-bottom: 0.5rem;
}

.preview-content :deep(table) {
  width: 100%;
  border-collapse: collapse;
  margin: 0.5rem 0;
}

.preview-content :deep(th),
.preview-content :deep(td) {
  border: 1px solid var(--bs-border-color);
  padding: 6px 10px;
  text-align: left;
}

.preview-content :deep(th) {
  background: var(--bs-tertiary-bg);
  font-weight: 600;
}

.preview-content :deep(code) {
  background: var(--bs-tertiary-bg);
  padding: 2px 6px;
  border-radius: 3px;
  font-size: 0.85em;
}

.preview-content :deep(pre) {
  background: var(--bs-tertiary-bg);
  padding: 12px;
  border-radius: 4px;
  overflow-x: auto;
}

.preview-modal-lg {
  max-width: 1100px;
  width: 95%;
}

.bloco-json {
  font-family: 'Consolas', 'Courier New', monospace;
  font-size: 0.8rem;
  line-height: 1.5;
  background: var(--bs-tertiary-bg);
  border: 1px solid var(--bs-border-color);
  border-radius: 4px;
  padding: 14px;
  overflow-x: auto;
  white-space: pre-wrap;
  word-break: break-word;
  color: var(--bs-body-color);
  max-height: 70vh;
  overflow-y: auto;
}

.preview-modal-footer {
  display: flex;
  justify-content: flex-end;
  padding: 12px 20px;
  border-top: 1px solid var(--bs-border-color);
  flex-shrink: 0;
  gap: 8px;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.spin {
  animation: spin 1s linear infinite;
}
</style>
